import { supabase } from '@/integrations/supabase/client';

/**
 * Generate a signed URL for files in private storage buckets
 * Uses Supabase's built-in signed URL feature for temporary access without headers
 */
export async function generateSecureFileUrl(bucket: string, filePath: string, options?: { testResponseId?: string }): Promise<string | null> {
  try {
    // For private buckets, generate a signed URL with 1 hour expiry
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      console.error(`Failed to generate signed URL for ${bucket}/${filePath}:`, error);
      return null;
    }

    if (!data?.signedUrl) {
      console.error(`No signed URL returned for ${bucket}/${filePath}`);
      return null;
    }

    console.log(`Generated signed URL for ${bucket}/${filePath}`);
    return data.signedUrl;
  } catch (error) {
    console.error(`Error generating signed URL for ${bucket}/${filePath}:`, error);
    return null;
  }
}

/**
 * Generate multiple secure file URLs for a list of file paths
 */
export async function generateMultipleSecureFileUrls(bucket: string, filePaths: string[]): Promise<Record<string, string>> {
  const urls: Record<string, string> = {};
  
  for (const filePath of filePaths) {
    const url = await generateSecureFileUrl(bucket, filePath);
    if (url) {
      urls[filePath] = url;
    }
  }
  
  return urls;
}

/**
 * Upload file to storage and return secure URL
 */
export async function uploadFileSecurely(
  bucket: string, 
  filePath: string, 
  file: File | Blob, 
  options: { contentType?: string; upsert?: boolean } = {}
): Promise<string | null> {
  try {
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        contentType: options.contentType,
        upsert: options.upsert || false
      });

    if (uploadError) {
      throw uploadError;
    }

    // Return secure URL instead of public URL
    return await generateSecureFileUrl(bucket, filePath);
  } catch (error) {
    console.error('Error uploading file securely:', error);
    return null;
  }
}

/**
 * Check if a bucket is private and requires secure access
 */
export function isPrivateBucket(bucket: string): boolean {
  return ['test-responses', 'cvs'].includes(bucket);
}

/**
 * Get appropriate URL for a file based on bucket privacy
 */
export async function getFileUrl(bucket: string, filePath: string): Promise<string | null> {
  if (isPrivateBucket(bucket)) {
    // Use signed URL for private buckets
    return await generateSecureFileUrl(bucket, filePath);
  } else {
    // For public buckets, use direct public URL
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  }
}